import React, { FC, MutableRefObject, useEffect, useState } from 'react';
import classNames from 'classnames';

// Components
import FloatingPanel from '../../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../../components/popupPanel/PopupPanelMouseManager';
import SimpleDateTimePicker from '../../../components/datetime/SimpleDateTimePicker';

// Constants
import { PopupIds } from '../../../components/popupPanel/popupConstants';
import { TooltipPositions } from '../../../components/tooltips/tooltipConstants';
import { CellTypeNames } from '../../../../common/table/CellTypeConstants';

// Utils
import {
    dateTimeStringHasSeconds,
    getDateFromString,
    toNumericDateTimeString,
} from '../../../../common/utils/timeUtil';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';
import { isNil } from 'lodash';
import { getAllCellsBetween } from '../utils/selectionUtils/tableSelectionGeometryUtils';
import getEditorJsonText from '../../../../common/tiptap/utils/jsonContentUtils/getEditorJsonText';

import { getHotTableCellData } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { MNHotInstance } from '../tableHotInstanceTypes';
import { DateTimeTypeObject, TypedCellData } from '../../../../common/table/TableTypes';

type Props = {
    locale: string;
    gridSize: number;
    element: ImMNElement;
    showFormulaDropdown: boolean;
    isCurrentlyEditingTableCell: boolean;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
};

// Implemented as a separate function just to allow for the various early return paths
const getEditedCellState = (hotInstance: MNHotInstance | null) => {
    if (!hotInstance) return null;

    const currentCellSelections = hotInstance.getSelected();
    const cellsBetween = getAllCellsBetween(currentCellSelections);
    if (cellsBetween.length !== 1) return null;

    const editingCellData = getHotTableCellData(hotInstance, cellsBetween[0]);
    if (!editingCellData) return null;

    const {
        type: { hasTime, name } = {},
        value,
        textStyle,
        draftContent,
    } = editingCellData as TypedCellData<DateTimeTypeObject>;

    const isDateTimeCell = name === CellTypeNames.DATE_TIME;
    const rawText = draftContent ? getEditorJsonText(draftContent) : value;

    return {
        isDateTimeCell,
        rawText,
        hasTime,
        textStyle,
    };
};

const DatePickerContainer: FC<Props> = (props) => {
    const { isCurrentlyEditingTableCell, gridSize, showFormulaDropdown, locale, hotTableInstanceRef } = props;

    const {
        isDateTimeCell = false,
        rawText = '',
        hasTime = false,
        textStyle = [],
    } = getEditedCellState(hotTableInstanceRef?.current) || {};
    const [hasTimeValue, setHasTimeValue] = useState(hasTime || false);

    // Get the date from the cell contents, or default to the current date
    const [calendarInputDate, setCalendarInputDate] = useState(
        () => getDateFromString(rawText, locale) || new Date(new Date().setHours(0, 0, 0, 0)),
    );

    const showDatePicker = isCurrentlyEditingTableCell && isDateTimeCell && !showFormulaDropdown && !isFormula(rawText);

    useEffect(() => {
        if (isNil(calendarInputDate) || !showDatePicker) return;

        const cellEditor = hotTableInstanceRef?.current?.getMNEditor();
        if (!cellEditor) return;

        // when date or hasTime changes, update the cell editor contents
        const content = cellEditor.getPlainTextContent();
        const hasSeconds = dateTimeStringHasSeconds(content);
        const numericDateTimeString = toNumericDateTimeString(
            calendarInputDate,
            {
                hasTime: hasTimeValue,
                hasSeconds,
                useTwelveHour: true,
            },
            locale,
        );

        cellEditor.setPlainTextContent(numericDateTimeString, textStyle);
    }, [calendarInputDate, hasTimeValue, locale, showDatePicker]);

    if (!showDatePicker) return null;

    return (
        <FloatingPanel
            selector=".Table.selected .handsontable-input-holder"
            className={classNames('TableDateTimePicker', 'className', 'light')}
            position={TooltipPositions.BOTTOM}
            gridSize={gridSize}
            pollPosition
        >
            <PopupPanelMouseManager
                {...props}
                visible
                popupId={PopupIds.TABLE_DATE_TIME_PICKER}
                ignoredClicksSelector=".Toolbar"
                closePopup={() => {}}
            >
                <div className="popup-content" data-table-util={true}>
                    <SimpleDateTimePicker
                        locale={locale}
                        date={calendarInputDate}
                        updateDate={(date) => setCalendarInputDate(date)}
                        hasTime={hasTimeValue}
                        updateDateHasTime={(hasTime) => setHasTimeValue(hasTime)}
                    />
                </div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

export default DatePickerContainer;
